import { z } from 'zod'

import type { MediaRatio, MediaWidth } from '@ems/storage/variants'

import { RICHTEXT_SOURCE_MAX, richTextLength } from '#cms/richtext'

/**
 * The field vocabulary — **closed, and closed on purpose**.
 *
 * Spec §8.2 puts "a general-purpose field-type system" on the list of things to refuse
 * explicitly, because it is one of the five features that turn a CMS into a multi-year
 * project. This file is where that refusal is made structural rather than remembered: there
 * are eleven kinds, they are declared here, and the only way to add a twelfth is to edit this
 * file and the editor that renders it in the same commit.
 *
 * The distinction worth holding onto is between a **field kind** and a **field type**. A type
 * system lets a tenant invent kinds at runtime, which means the admin UI has to render
 * something it has never seen, the database has to store a shape nothing validates, and every
 * query over content becomes dynamic. A vocabulary is the opposite: eleven known shapes, each
 * with an editor somebody designed, each queryable, each validated by a Zod schema the API and
 * the website both hold.
 *
 * **Every field must declare its kind.** Not inferred from the Zod type — `z.string()` is a
 * headline, a slug, a paragraph of markdown, an S3 key and a URL, and guessing between them
 * produces a `<input type="text">` where an editor expected a media picker. `describeCollection`
 * throws at definition time on a property with no kind, so the failure is a boot error with a
 * field name in it rather than a wrong control on a form.
 */

export const FIELD_KINDS = [
  /** One line of plain text: a name, a job title, a headline. */
  'line',
  'text',
  /** Markdown. The only field the website renders as HTML, and the only one that needs escaping. */
  'richtext',
  'number',
  'boolean',
  /** An ISO-8601 instant with an offset. Never a naive date — see `packages/db`'s `timestamps`. */
  'date',
  /** An S3 object key, joined to `cms_media` for alt text and dimensions. Never a URL. */
  'media',
  /** One of a fixed list, declared in the schema. Renders as a select. */
  'select',
  /** An ordered list of single-line strings. Renders as a repeater, not as a comma-separated box. */
  'list',
  /**
   * An ordered list of S3 object keys — a gallery.
   *
   * The twelfth kind, and it exists because its absence was a bug rather than because a gallery
   * wanted its own editor. `exhibitionGalleryKeys` was a plain `list` whose label said "(S3
   * keys)", so the intent lived in prose: the Payload import maps a media reference to a key
   * only for fields it can *see* are media, saw a `list`, and copied Payload's numeric ids
   * through verbatim. The published page then asked for `/media/40` and got a 404 per image.
   *
   * A label is not a type. This is.
   */
  'medialist',
  /**
   * An ordered list of absolute `http(s)` URLs to somewhere else — social profiles, mostly.
   *
   * The thirteenth kind, for the same reason as `medialist`: `headerSocialLinks` was a plain
   * `list` whose comment said "one URL per entry", so nothing refused an entry that was not one,
   * and nothing could tell the editor or the website that the strings were links whose host
   * names a platform. The editor shows each entry's platform icon; the website renders it.
   */
  'urllist',
  /** An absolute URL to somewhere else. */
  'url',
  'email',
  /**
   * The ordered sections of a page, each one from a fixed list (`layouts.ts`).
   *
   * Its own kind, not a `list`: a `list` renders as a repeater of text boxes, and a section id
   * typed by hand is a page that renders nothing. The editor renders this kind as the page's
   * layout, a list of cards that an editor drags, adds and removes.
   */
  'sections',
] as const

export type FieldKind = (typeof FIELD_KINDS)[number]

export const MEDIA_ACCEPTS = ['image', 'file'] as const

export type MediaAccept = (typeof MEDIA_ACCEPTS)[number]

export function isMediaAccept(value: unknown): value is MediaAccept {
  return typeof value === 'string' && (MEDIA_ACCEPTS as readonly string[]).includes(value)
}

export type MediaSlot = {
  ratio: MediaRatio | null
  widths: readonly MediaWidth[]
  sizes: string
}

export function isFieldKind(value: unknown): value is FieldKind {
  return typeof value === 'string' && (FIELD_KINDS as readonly string[]).includes(value)
}

/**
 * How a field is labeled and helped in the admin UI.
 *
 * Carried in Zod's own metadata rather than in a parallel object keyed by field name. A
 * parallel object is a second list to keep in sync, and the failure when it drifts is a form
 * with a label from the field above it.
 */
export type FieldMeta = {
  field: FieldKind
  label?: string
  help?: string
  /** Renders wide in the editor's two-column form. Sensible for richtext and text. */
  wide?: boolean
  group?: string
  accept?: MediaAccept
  slot?: MediaSlot
  textMax?: number
}

/**
 * `.meta()` returns a new schema of the same class, so the caller's narrow type survives — which
 * is what keeps `z.infer` on a collection precise rather than widening every field to `ZodType`.
 */
function withMeta<S extends z.ZodType>(schema: S, meta: FieldMeta): S {
  return schema.meta({ ...meta })
}

/**
 * Everything a field declares besides its kind.
 *
 * **One options object, including `max`**, rather than a positional length followed by an
 * options bag. The positional form reads fine for `f.line(200)` and stops reading the moment a
 * field wants a label but the default length — `f.richtext({ label: 'Footer' })` then silently
 * passes an object where a number belongs. It did, in the first draft of `collections.ts`, and
 * the typechecker caught it; the second time it would be a field with no maximum.
 */
type Opts = Omit<FieldMeta, 'field' | 'accept' | 'slot' | 'textMax'> & { max?: number }

type MediaOpts = Opts & { accept?: MediaAccept; slot?: MediaSlot }

function rest(opts: Opts | MediaOpts): Omit<FieldMeta, 'field'> {
  const { max: _max, ...meta } = opts
  return meta
}

/**
 * `packages/storage` composes object keys as `tenants/{id}/{scope}/{objectId}/{filename}`, and
 * the longest of those is bounded by the filename cap at 120 characters. 1024 is S3's own
 * limit and leaves the bound where S3 puts it rather than where this file guesses.
 */
export const S3_KEY_MAX = 1024

/**
 * One entry of a `urllist`: absolute, and `http` or `https` only.
 *
 * `z.url()` alone accepts any scheme, and `javascript:` is a scheme. A list of links an editor
 * pastes is rendered as `<a href>` on the public site, so the scheme is checked where the data
 * is accepted as well as where it is rendered. Exported so the Payload import drops exactly the
 * entries this would refuse, rather than failing the whole page on one of them.
 */
export const linkUrlSchema = z.url({ protocol: /^https?$/ }).max(400)

export const f = {
  line: (opts: Opts = {}) =>
    withMeta(z.string().max(opts.max ?? 200), { field: 'line', ...rest(opts) }),

  text: (opts: Opts = {}) =>
    withMeta(z.string().max(opts.max ?? 4000), { field: 'text', wide: true, ...rest(opts) }),

  richtext: (opts: Opts = {}) => {
    const max = opts.max ?? RICHTEXT_SOURCE_MAX
    return withMeta(
      z
        .string()
        .max(RICHTEXT_SOURCE_MAX)
        .refine((value) => richTextLength(value) <= max, {
          error: `Too big: expected text to have <=${max} characters`,
        }),
      { field: 'richtext', wide: true, textMax: max, ...rest(opts) },
    )
  },

  number: (opts: Opts = {}) => withMeta(z.number(), { field: 'number', ...rest(opts) }),

  boolean: (opts: Opts = {}) => withMeta(z.boolean(), { field: 'boolean', ...rest(opts) }),

  date: (opts: Opts = {}) =>
    withMeta(z.iso.datetime({ offset: true }), { field: 'date', ...rest(opts) }),

  /**
   * An S3 object **key**, and the name says so.
   *
   * `*Key`, never `*Url`, and the rule is older than this module: a stored URL is a presigned
   * capability with an expiry baked into the data, so a row written on Tuesday serves a broken
   * image on Wednesday. The key is durable; the URL is minted per read.
   */
  media: (opts: MediaOpts = {}) =>
    withMeta(z.string().min(1).max(S3_KEY_MAX), {
      field: 'media',
      accept: 'image',
      ...rest(opts),
    }),

  select: <const T extends readonly [string, ...string[]]>(values: T, opts: Opts = {}) =>
    withMeta(z.enum(values), { field: 'select', ...rest(opts) }),

  list: (opts: Opts = {}) =>
    withMeta(z.array(z.string().max(200)).max(opts.max ?? 50), {
      field: 'list',
      wide: true,
      ...rest(opts),
    }),

  /** A gallery: `media`, repeated. Same `*Key` rule, same reason. */
  medialist: (opts: MediaOpts = {}) =>
    withMeta(z.array(z.string().min(1).max(S3_KEY_MAX)).max(opts.max ?? 50), {
      field: 'medialist',
      wide: true,
      accept: 'image',
      ...rest(opts),
    }),

  urllist: (opts: Opts = {}) =>
    withMeta(z.array(linkUrlSchema).max(opts.max ?? 50), {
      field: 'urllist',
      wide: true,
      ...rest(opts),
    }),

  url: (opts: Opts = {}) => withMeta(z.url(), { field: 'url', ...rest(opts) }),

  email: (opts: Opts = {}) => withMeta(z.email(), { field: 'email', ...rest(opts) }),

  /** An ordered choice from `values`, each at most once. */
  sections: <const T extends readonly [string, ...string[]]>(values: T, opts: Opts = {}) =>
    withMeta(
      z
        .array(z.enum(values))
        .max(values.length)
        .refine((ids) => new Set(ids).size === ids.length, 'A section is on the page twice.'),
      { field: 'sections', wide: true, ...rest(opts) },
    ),
}

/**
 * The slug rule, in one place because three things depend on it agreeing with itself: the
 * editor's validation, the `(tenant_id, collection, slug, locale)` unique index, and the
 * redirect written when a slug changes.
 *
 * Lowercase, digits, hyphens; no leading, trailing or doubled hyphen. Deliberately **not**
 * allowing slashes: a slug is one path segment, and letting it carry `/` makes
 * `collection + slug` ambiguous about where the document actually lives.
 */
export const slugSchema = z
  .string()
  .min(1)
  .max(160)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'lowercase letters, digits and single hyphens only')
